// Embedded (ComplyControl iframe) mode — COM-636.
//
// The backend serves the app from POST /embed/bootstrap with a
// `window.__EMBED_BOOTSTRAP__` script tag holding a short-lived session
// token. The token stays in this module's memory only: never localStorage,
// never sessionStorage, never a cookie — a cross-domain iframe cannot rely on
// cookies, and persisting a bearer token would outlive the iframe that owns
// it. Reloading the CC page runs the handshake again.

export type EmbedView = 'articles' | 'settings';

export interface EmbedBootstrap {
  token: string;
  view: EmbedView;
}

declare global {
  interface Window {
    __EMBED_BOOTSTRAP__?: { token?: unknown; view?: unknown };
  }
}

const EMBED_VIEWS: EmbedView[] = ['articles', 'settings'];

function readBootstrap(): EmbedBootstrap | null {
  const raw = typeof window === 'undefined' ? undefined : window.__EMBED_BOOTSTRAP__;

  if (!raw || typeof raw.token !== 'string' || raw.token === '') {
    return null;
  }

  const view = EMBED_VIEWS.includes(raw.view as EmbedView) ? (raw.view as EmbedView) : 'articles';

  return { token: raw.token, view };
}

// Captured once at module load, then removed from the global object so no
// other script on the page can read the token back out of `window`.
const bootstrap: EmbedBootstrap | null = readBootstrap();

if (bootstrap && typeof window !== 'undefined') {
  delete window.__EMBED_BOOTSTRAP__;
}

export function isEmbedded(): boolean {
  return bootstrap !== null;
}

export function getEmbedToken(): string | null {
  return bootstrap?.token ?? null;
}

export function getEmbedView(): EmbedView | null {
  return bootstrap?.view ?? null;
}
